import { api } from "./api";

export interface Application {
  _id: string;
  user: string;
  applicationType: "instructor" | "mentor";
  headline: string;
  bio: string;
  expertise: string[];
  yearsOfExperience: number;
  teachingStyle?: string;
  mentoringTopics?: string[];
  availableDays?: string[];
  availableTimeSlots?: string;
  previousTeachingExperience?: string;
  notableAchievements?: string;
  publishedContent?: string;
  portfolioUrl?: string;
  linkedInUrl?: string;
  phone?: string;
  status: "pending" | "under_review" | "approved" | "rejected" | "shortlisted";
  reviewNotes?: string;
  rejectionReason?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ApplicationStats {
  total: number;
  pending: number;
  under_review: number;
  approved: number;
  rejected: number;
  shortlisted: number;
}

export const applicationApi = {
  // Submit an application to become a mentor or instructor
  applyForRole: (data: {
    applicationType: "instructor" | "mentor";
    headline: string;
    bio: string;
    expertise: string | string[];
    yearsOfExperience: number;
    teachingStyle?: string;
    mentoringTopics?: string | string[];
    availableDays?: string | string[];
    availableTimeSlots?: string;
    previousTeachingExperience?: string;
    notableAchievements?: string;
    publishedContent?: string;
    portfolioUrl?: string;
    linkedInUrl?: string;
    phone?: string;
  }) => {
    return api.post<{ success: boolean; message: string; data: Application }>(
      "/applications/apply",
      data
    );
  },

  // Get my applications
  getMyApplications: () => {
    return api.get<{ success: boolean; count: number; data: Application[] }>(
      "/applications/my-applications"
    );
  },

  // Get single application by ID
  getMyApplication: (applicationId: string) => {
    return api.get<{ success: boolean; data: Application }>(
      `/applications/my-applications/${applicationId}`
    );
  },

  // Get all applications (admin only)
  getAllApplications: (options?: {
    page?: number;
    limit?: number;
    applicationType?: "instructor" | "mentor";
    status?: "pending" | "under_review" | "approved" | "rejected" | "shortlisted";
    search?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.applicationType) params.append("applicationType", options.applicationType);
    if (options?.status) params.append("status", options.status);
    if (options?.search) params.append("search", options.search);

    return api.get<{
      success: boolean;
      data: Application[];
      pagination: any;
    }>(
      `/applications${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  // Review an application (admin only)
  reviewApplication: (
    applicationId: string,
    data: {
      status: "pending" | "under_review" | "approved" | "rejected" | "shortlisted";
      reviewNotes?: string;
      rejectionReason?: string;
    }
  ) => {
    return api.put<{ success: boolean; message: string; data: Application }>(
      `/applications/${applicationId}/review`,
      data
    );
  },

  // Get application stats (admin only)
  getApplicationStats: () => {
    return api.get<{ success: boolean; data: ApplicationStats }>(
      "/applications/stats"
    );
  },
};